/**
 * The shell's session credential: ONE bearer JWT (EdDSA, `sub` = the person's public id) — signed by
 * Pinksy for a password sign-in (D53) or by the Passport for a passkey one — with the opaque REFRESH
 * token beside it. Nothing here reads either: `auth/verify.ts` checks the signature and expiry
 * against the right JWKS before the store trusts a single claim, and the APIs verify again on every
 * request.
 *
 * 🔴 **KEPT IN `localStorage`, NOT `sessionStorage`** (D83) — changed deliberately, and this is the
 * paragraph to read before changing it back.
 *
 * Devin: *"Every time I refresh the page, it seems like I lose access… We need these sessions to be
 * running twenty four hours before it forces them to re sign in or even three days if it's from the
 * same machine."* A session that must last a day cannot live in a store the browser empties when the
 * tab closes — `sessionStorage` is per-tab, so opening the app in a second tab was a fresh sign-in
 * and closing the last one ended a session that had twenty-three hours left. Asking for a day and
 * storing it per-tab is asking for a day and giving an afternoon.
 *
 * 🔴 **The trade, stated honestly:** the token is now readable by anything running on this origin and
 * survives until it expires or somebody signs out. That is what "stay signed in" costs everywhere,
 * and it is bounded on three sides — the ACCESS token still lasts fifteen minutes, the refresh token
 * is a revocable row with an absolute end (`services/auth/sessions.ts`), and signing out clears both
 * here and revokes the row there.
 *
 * 🔴 **What did NOT move:** the chosen organisation (`stores/organisation.ts`) stays per-tab on
 * purpose, so an agent can have two companies open side by side. Session and context are different
 * questions, and it was only ever the session that Devin was losing.
 *
 * 🔴 **AN ASSISTED SESSION IS STILL PER-TAB** (D34). Wearing somebody else's session must never
 * outlive the tab it was opened in — a support call that is still live tomorrow morning is exactly
 * how that feature becomes an incident. So a token can be held VOLATILE (`setToken(token, false)`),
 * which puts it in `sessionStorage` and leaves the durable one alone: close the tab and the operator
 * is themselves again, holding the session they had before the assist.
 */
const STORAGE_KEY = "pinksy:session:token";
/**
 * The refresh token beside it: opaque, single-use, and the ONLY credential `POST /auth/refresh`
 * accepts. Without it a session simply lapses at `exp` — which is exactly what was happening to every
 * password sign-in before D83, because Pinksy issued none.
 */
const REFRESH_STORAGE_KEY = "pinksy:session:refresh";

let inMemory: string | null = null;
let inMemoryRefresh: string | null = null;
let hydrated = false;
let refreshHydrated = false;

/** Where a session that should outlive the tab lives (D83). */
function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Where a session that must NOT outlive the tab lives — an assist, and only an assist (D34). */
function volatileStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * 🔴 The per-tab copy WINS when both exist, and that is what makes an assist safe: while an operator
 * is wearing somebody else's session, their own durable one is still sitting in `localStorage`
 * untouched, waiting for the tab to close.
 */
function read(key: string): string | null {
  try {
    return volatileStorage()?.getItem(key) ?? storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

/** The current bearer JWT, or null when signed out. */
export function getToken(): string | null {
  if (!hydrated) {
    hydrated = true;
    inMemory = read(STORAGE_KEY);
  }
  return inMemory;
}

/**
 * Hold a freshly issued token. Durable by default (D83) — it outlives the tab, and the session's own
 * end is the server's. `durable: false` holds it for THIS TAB ONLY, which is what an assist takes.
 */
export function setToken(token: string, durable = true): void {
  hydrated = true;
  inMemory = token;
  try {
    if (durable) {
      storage()?.setItem(STORAGE_KEY, token);
      // A durable session replaces any per-tab one, or the tab copy would keep winning the read.
      volatileStorage()?.removeItem(STORAGE_KEY);
    } else {
      volatileStorage()?.setItem(STORAGE_KEY, token);
    }
  } catch {
    // best-effort — the session then lasts until the next full reload
  }
}

/** The current refresh token, or null when the sign-in that issued the session handed out none. */
export function getRefreshToken(): string | null {
  if (!refreshHydrated) {
    refreshHydrated = true;
    inMemoryRefresh = read(REFRESH_STORAGE_KEY);
  }
  return inMemoryRefresh;
}

/** Hold the refresh token just issued — it ROTATES on every refresh, so this replaces the last one. */
export function setRefreshToken(token: string | null, durable = true): void {
  refreshHydrated = true;
  inMemoryRefresh = token;
  try {
    if (token === null) {
      storage()?.removeItem(REFRESH_STORAGE_KEY);
      volatileStorage()?.removeItem(REFRESH_STORAGE_KEY);
      return;
    }
    if (durable) {
      storage()?.setItem(REFRESH_STORAGE_KEY, token);
      volatileStorage()?.removeItem(REFRESH_STORAGE_KEY);
    } else {
      volatileStorage()?.setItem(REFRESH_STORAGE_KEY, token);
    }
  } catch {
    // best-effort — the session then lapses at `exp` instead of renewing
  }
}

/** Forget both tokens — sign-out, or a token found expired at boot. */
export function clear(): void {
  hydrated = true;
  refreshHydrated = true;
  inMemory = null;
  inMemoryRefresh = null;
  try {
    for (const store of [storage(), volatileStorage()]) {
      store?.removeItem(STORAGE_KEY);
      store?.removeItem(REFRESH_STORAGE_KEY);
    }
  } catch {
    // nothing to forget
  }
}
